import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { orderService } from '../services/order.service';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/ToastProvider';

export const CheckoutPaymentPage: React.FC = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [status, setStatus] = useState<'loading' | 'success' | 'failed'>('loading');
  const reference = searchParams.get('reference');

  const verifyPaymentMutation = useMutation({
    mutationFn: () => orderService.verifyPayment(parseInt(id!), reference!),
    onSuccess: () => {
      setStatus('success');
      showToast('Payment successful!', 'success');
      setTimeout(() => navigate('/orders'), 3000);
    },
    onError: () => {
      setStatus('failed');
      showToast('Payment verification failed', 'error');
    },
  });

  useEffect(() => {
    if (reference && id) {
      verifyPaymentMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference, id]);

  if (status === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="mt-4 text-[rgb(var(--light-secondary))]">Verifying your payment...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4">
      {status === 'success' ? (
        <>
          <CheckCircle className="h-16 w-16 text-green-500" />
          <h2 className="mt-4 text-2xl font-bold text-[rgb(var(--foreground))]">Payment Successful!</h2>
          <p className="text-[rgb(var(--light-secondary))]">Your order has been confirmed.</p>
          <Button variant="primary" className="mt-6" onClick={() => navigate('/orders')}>
            View Orders
          </Button>
        </>
      ) : (
        <>
          <XCircle className="h-16 w-16 text-red-500" />
          <h2 className="mt-4 text-2xl font-bold text-[rgb(var(--foreground))]">Payment Failed</h2>
          <p className="text-[rgb(var(--light-secondary))]">Please try again or contact support.</p>
          <Button variant="primary" className="mt-6" onClick={() => navigate(`/checkout`)}>
            Retry
          </Button>
        </>
      )}
    </div>
  );
};